// Builds Collab's page elements in the person's language and refreshes their text when the language
// is switched, without rebuilding anything the person is using; also holds Collab's icons and the
// small pieces every screen shares: icon buttons, two-line labels, avatars, panel headers and the
// copy action.
//
// Strings become text nodes, never markup. A child or an attribute given as a function is
// translated text: it is computed now and again when the language changes, so a language switch
// keeps every live element (players, notes, focus).

import { hueOf, startLanguage, text, type Key, type Lang } from './text.ts';

const LANGUAGE = 'collab-language';

function stored(): string | null {
  try {
    return localStorage.getItem(LANGUAGE);
  } catch {
    return null;
  }
}

export let lang: Lang = startLanguage(stored(), navigator.languages ?? [navigator.language]);
document.documentElement.lang = lang;

/** The text for `key` in the current language. */
export const t = (key: Key, values?: Record<string, string | number>) => text(lang, key, values);

/** Every translated text and attribute, until it leaves the document. */
const relabels = new Set<{ node: Node; update: () => void }>();

function track(node: Node, update: () => void): void {
  // Lists render again as they change; forget what left the document now and then.
  if (relabels.size > 2_000) for (const relabel of relabels) if (!relabel.node.isConnected) relabels.delete(relabel);
  relabels.add({ node, update });
}

/** Sets the language, keeps the choice in this browser, and relabels the page. */
export function setLanguage(next: Lang): void {
  lang = next;
  document.documentElement.lang = lang;
  try {
    localStorage.setItem(LANGUAGE, lang);
  } catch {
    // Kept for this page only.
  }
  for (const relabel of [...relabels]) {
    if (relabel.node.isConnected) relabel.update();
    else relabels.delete(relabel);
  }
}

/** A text node whose text is `fn()` in the current language. */
export function tx(fn: () => string): Text {
  const node = document.createTextNode(fn());
  track(node, () => (node.data = fn()));
  return node;
}

export type Child = Node | string | (() => string) | null | undefined | false | Child[];
export type Attrs = Record<string, string | boolean | undefined | (() => string) | ((event: never) => void)>;

const flat = (children: Child[]): (Node | string)[] => children.flatMap(child => {
  if (Array.isArray(child)) return flat(child);
  if (child === null || child === undefined || child === false) return [];
  return typeof child === 'function' ? [tx(child)] : [child];
});

/** An element: `on…` attributes are listeners, functions are translated attributes, `true` sets an empty one. */
export function h<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Attrs = {}, ...children: Child[]): HTMLElementTagNameMap[K] {
  const element = document.createElement(tag);
  for (const [name, value] of Object.entries(attrs)) {
    if (name.startsWith('on') && typeof value === 'function') {
      element.addEventListener(name.slice(2), value as EventListener);
    } else if (typeof value === 'function') {
      const update = () => element.setAttribute(name, (value as () => string)());
      update();
      track(element, update);
    } else if (value === true) {
      element.setAttribute(name, '');
    } else if (typeof value === 'string') {
      element.setAttribute(name, value);
    }
  }
  element.append(...flat(children));
  return element;
}

/** Replaces the children of `element`. */
export function show(element: Element, ...children: Child[]): void {
  element.replaceChildren(...flat(children));
}

/** The language choice, offered on every screen: each language named in itself. */
export function languageSelect(): HTMLSelectElement {
  const select: HTMLSelectElement = h('select', { id: 'language', 'aria-label': () => t('language'), onchange: () => setLanguage(select.value as Lang) },
    h('option', { value: 'en', lang: 'en' }, 'English'), h('option', { value: 'ja', lang: 'ja' }, '日本語'));
  select.value = lang;
  return select;
}

/** Collab's line icons: one SVG path each on a 24-pixel square, stroked by the button's color. */
const ICONS = {
  'mic': 'M12 3a3 3 0 0 1 3 3v6a3 3 0 0 1-6 0V6a3 3 0 0 1 3-3zM5 11a7 7 0 0 0 14 0M12 18v3M8 21h8',
  'mic-off': 'M15 10V6a3 3 0 0 0-5.6-1.5M9 9v3a3 3 0 0 0 5.1 2.1M5 11a7 7 0 0 0 11.4 5.4M18.6 14A7 7 0 0 0 19 11M12 18v3M8 21h8M3 3l18 18',
  'camera': 'M3 7a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1zM15 10l6-3v10l-6-3',
  'camera-off': 'M3 7a1 1 0 0 1 1-1h2M11 6h3a1 1 0 0 1 1 1v3M15 10l6-3v10M15 15v2a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8M3 3l18 18',
  'screen': 'M3 5h18v11H3zM8 20h8M12 16v4M12 13V8M9.5 10.5L12 8l2.5 2.5',
  'sliders': 'M4 7h9M17 7h3M4 17h3M11 17h9M13 4.5v5M7 14.5v5',
  'board': 'M4 4h16v10l-6 6H4zM14 20v-6h6',
  'paperclip': 'M21 11.5l-8.5 8.5a5 5 0 0 1-7-7l9-9a3.5 3.5 0 0 1 5 5l-9 9a2 2 0 0 1-3-3l8-8',
  'people': 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8',
  'dots': 'M5 11a1 1 0 1 0 0 2 1 1 0 0 0 0-2zM12 11a1 1 0 1 0 0 2 1 1 0 0 0 0-2zM19 11a1 1 0 1 0 0 2 1 1 0 0 0 0-2z',
  'leave': 'M3 13c5-5 13-5 18 0M3 13l2 3 3-2-1-3M21 13l-2 3-3-2 1-3',
  'close': 'M6 6l12 12M18 6L6 18',
  'fullscreen': 'M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5',
  'pip': 'M3 5h18v14H3zM12 12h6v4h-6z',
  'copy': 'M8 8h12v12H8zM16 8V4H4v12h4',
  'plus': 'M12 5v14M5 12h14',
  'open': 'M14 4h6v6M20 4l-9 9M19 14v5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5',
  'save': 'M12 4v12M7 11l5 5 5-5M4 20h16',
  'file': 'M6 3h8l5 5v13H6zM14 3v5h5',
  'note': 'M4 4h16v10l-6 6H4zM14 20v-6h6M8 9h8M8 13h4',
  'image': 'M4 5h16v14H4zM4 16l5-5 4 4 3-3 4 4M15 9a1 1 0 1 0 2 0 1 1 0 1 0-2 0',
  'link': 'M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.5 1.5M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.5-1.5',
  'globe': 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM3 12h18M12 3c2.4 2.6 3.6 5.6 3.6 9s-1.2 6.4-3.6 9M12 3c-2.4 2.6-3.6 5.6-3.6 9s1.2 6.4 3.6 9',
  'pin': 'M9 3h6v6l2 3H7l2-3zM12 12v9',
};
export type IconName = keyof typeof ICONS;

/** The line icon `name`, hidden from assistive technology (the button's label names it). */
export function icon(name: IconName): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS(svg.namespaceURI, 'path');
  path.setAttribute('d', ICONS[name]);
  svg.append(path);
  return svg;
}

/** Changes which icon `svg` draws. */
export function setIcon(svg: SVGSVGElement, name: IconName): void {
  svg.querySelector('path')?.setAttribute('d', ICONS[name]);
}

/** A round icon button labelled `label` (its `aria-label` and `title`), with `caption` under the icon when given. */
export function iconButton(attrs: Attrs, name: IconName, label: Key, caption?: Child): HTMLButtonElement {
  return h('button', { type: 'button', ...attrs, class: `icon${attrs.class ? ` ${attrs.class}` : ''}`, 'aria-label': () => t(label), title: () => t(label) },
    icon(name), caption && h('span', { class: 'cap' }, caption));
}

/** A two-line label, `name` over `detail`, each line cut with an ellipsis: a shared file's row and the board's cards use it. */
export function nameDetail(name: string, detail: Child): HTMLElement {
  return h('span', { class: 'name-detail' }, h('span', { class: 'name' }, name), h('span', { class: 'muted' }, detail));
}

/**
 * A disc with `text` colored from `seed`: `.avatar.small` or `.avatar.large` on its own, or without `size`
 * the layer that fills a tile (`div.avatar > span`), hidden while the tile shows a picture.
 */
export function avatar(seed: string, text: string, size?: 'small' | 'large'): HTMLElement {
  const style = `--hue: ${hueOf(seed)}`;
  if (size) return h('span', { class: `avatar ${size}`, style, 'aria-hidden': 'true' }, text);
  return h('div', { class: 'avatar', style, 'aria-hidden': 'true' }, h('span', {}, text));
}

/** A panel's header: its title (focused when the panel opens), its actions, and a close button. */
export function panelHead(title: Child, ...actions: Child[]): HTMLElement {
  return h('header', { class: 'panel-head' }, h('h2', { tabindex: '-1' }, title), actions,
    iconButton({ class: 'panel-close' }, 'close', 'close'));
}

/** A copy action for `value`, labelled `label`, with its result. */
export function copyButton(value: () => string, label: Key = 'copy'): Node {
  const status = h('span', { class: 'muted', 'aria-live': 'polite' });
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value());
      show(status, () => t('copied'));
    } catch {
      show(status, () => t('copyFailed'));
    }
  };
  return h('span', { class: 'copy' }, h('button', { type: 'button', onclick: copy }, icon('copy'), () => t(label)), ' ', status);
}
